import { useState, useEffect, useContext } from 'react';
import { TrackingOrder } from '../models/TrackingOrder';
import { TrackingOrderItem } from '../models/TrackingOrderItem';
import TrackingOrderService from '../services/trackingOrderService';
import showErrorToast from '../components/Toasts/ShowErrorToast';
import { UserContext } from '../context/userContext';

export const useTrackingOrder = () => {
    const [trackingOrders, setTrackingOrders] = useState<TrackingOrder[]>([]);
    const [staleOrders, setStaleOrders] = useState<TrackingOrder[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { currentUser } = useContext(UserContext);

    const fetchTrackingOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const warehouseId = currentUser?.warehouse_hq !== '1' ? currentUser?.warehouse_id?.toString() : null;
            const data = await TrackingOrderService.getAll(warehouseId);
            setTrackingOrders(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch tracking orders');
            showErrorToast('Failed to fetch tracking orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchStaleNoDeliveryManUpdate = async () => {
        try {
            const warehouseId = currentUser?.warehouse_hq !== '1' ? currentUser?.warehouse_id?.toString() : null;
            const data = await TrackingOrderService.getStaleNoDeliveryManUpdate(warehouseId, 3);
            setStaleOrders(Array.isArray(data) ? data : []);
        } catch {
            setStaleOrders([]);
        }
    };

    const fetchTrackingOrderById = async (id: number): Promise<TrackingOrder | null> => {
        setLoading(true);
        try {
            return await TrackingOrderService.getById(id);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch tracking order');
            showErrorToast('Failed to fetch tracking order');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchByDeliveryManToken = async (token: string): Promise<TrackingOrder | null> => {
        setLoading(true);
        try {
            return await TrackingOrderService.getByDeliveryManToken(token);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch order');
            showErrorToast('Failed to fetch order');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchByCustomerToken = async (token: string): Promise<TrackingOrder | null> => {
        setLoading(true);
        try {
            return await TrackingOrderService.getByCustomerToken(token);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch order');
            showErrorToast('Failed to fetch order');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchBySubDeliveryManToken = async (token: string): Promise<TrackingOrder | null> => {
        setLoading(true);
        try {
            return await TrackingOrderService.getBySubDeliveryManToken(token);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch order');
            showErrorToast('Failed to fetch order');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const createTrackingOrder = async (orderData: { customer_id: number; warehouse_id?: number | null; delivery_number?: string | null; remark?: string | null }, items: TrackingOrderItem[]) => {
        try {
            const result = await TrackingOrderService.create(orderData, items);
            setTrackingOrders(prev => [...prev, result.data]);
            return result;
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to create tracking order');
            throw err;
        }
    };

    const updateTrackingOrder = async (id: number, orderData: { customer_id: number; warehouse_id?: number | null; delivery_number?: string | null; remark?: string | null }, items: TrackingOrderItem[]) => {
        try {
            const result = await TrackingOrderService.update(id, orderData, items);
            setTrackingOrders(prev => prev.map(o => o.id === id ? result.data : o));
            return result;
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to update tracking order');
            throw err;
        }
    };

    const deleteTrackingOrder = async (id: number) => {
        try {
            await TrackingOrderService.delete(id);
            setTrackingOrders(prev => prev.filter(o => o.id !== id));
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to delete tracking order');
            throw err;
        }
    };

    const createDeliveryManConfirmation = async (token: string, data: { driver_name: string }, image: File | null) => {
        try {
            return await TrackingOrderService.createDeliveryManConfirmation(token, data, image);
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to confirm delivery');
            throw err;
        }
    };

    const createSubDeliveryManConfirmation = async (token: string, data: { driver_name: string; vehicle_no?: string; notes?: string }, image: File | null) => {
        try {
            return await TrackingOrderService.createSubDeliveryManConfirmation(token, data, image);
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to confirm sub-delivery');
            throw err;
        }
    };

    const createCustomerConfirmation = async (token: string, data: { recipient_name: string; notes?: string }, image: File | null, signatureImage?: File | null) => {
        try {
            return await TrackingOrderService.createCustomerConfirmation(token, data, image, signatureImage ?? null);
        } catch (err: any) {
            showErrorToast(err?.message || 'Failed to confirm receipt');
            throw err;
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchTrackingOrders();
            fetchStaleNoDeliveryManUpdate();
        }
    }, [currentUser]);

    return {
        trackingOrders,
        staleOrders,
        loading,
        error,
        fetchTrackingOrders,
        fetchStaleNoDeliveryManUpdate,
        fetchTrackingOrderById,
        fetchByDeliveryManToken,
        fetchBySubDeliveryManToken,
        fetchByCustomerToken,
        createTrackingOrder,
        updateTrackingOrder,
        deleteTrackingOrder,
        createDeliveryManConfirmation,
        createSubDeliveryManConfirmation,
        createCustomerConfirmation,
    };
};
